import React from "react";

import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { AlertTriangle, ArrowUpRight } from "lucide-react";

import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import {
  REFRESH_MODE_FILTER_OPTIONS,
  REFRESH_OUTCOME_FILTER_OPTIONS,
  moodEventHref,
  refreshChangeKindLabel,
  refreshModeLabel,
  refreshOutcomeClasses,
  refreshOutcomeLabel,
} from "@/lib/tina/refresh-jobs";
import { cn } from "@/lib/utils";
import { ItemStatusBadge } from "@/pages/tina/components/item-status-badge";
import { formatUsdCost } from "@/pages/tina/lib/format-cost";
import type { RefreshJobRow } from "@/types/tina";

/**
 * Columns for the refresh-jobs table on a refresh run's show page.
 *
 * Read off `v_refresh_jobs` (`GET /refresh-jobs`), not off `events`, because the four columns a
 * curator actually scans here — mode, the derived outcome, the proposal/applied counts and the
 * page-gone flag — are jsonb projections that `GET /runs/{id}/events` returns only as raw
 * `metadata`.
 *
 * **Which columns may carry a filter is decided by the route, not by taste.** `GET /refresh-jobs`
 * filters on `run_id`, `status`, `refresh_outcome`, `mode`, `site` and `admin_event_id` — and the
 * three enum-shaped ones are typed, so an unknown value is a 422. Every other column here is
 * therefore display-only (`enableColumnFilter: false`), the same honesty the enrichment run
 * columns keep: a control the server ignores is worse than no control. Sorting is off everywhere
 * because the route has no `sort` param at all; the order is fixed at `created_at DESC, id DESC`.
 *
 * `scope` picks which of those columns earn their width:
 *
 * - `"run"` — the run drill-down, where a job can be in any state and the outcome mix is the
 *   point;
 * - `"review"` — `/tina/refresh-review`, where `status` is pinned to `hitl` and the outcome is
 *   therefore `hitl` on every row. Both would be constant columns carrying a filter the pinned
 *   `permanent` entry overrides, so they are dropped rather than shown inert; `cost` goes with
 *   them because a park is a decision to make, not a spend to audit. `mode` keeps its column but
 *   loses its filter — the page's own toolbar owns that axis.
 *
 * **Ruling R77 (2026-09-08) rebuilt the review set for a curator rather than an engineer.** The
 * owner compared the review queue with `/tina/hitl` — "the hitl queue one is better, it's more
 * large and clear" — and the diagnosis was reuse, not design: this hook was written for the runs
 * drill-down and the review page simply inherited its density, so a curator got Job / Mode / Site /
 * Mood event / Source / Proposed-applied / Page / Seeded, led by a truncated uuid. The review scope
 * now leads with `event` (the published event's NAME) and `review_kinds` (what still needs
 * deciding) — the two-line lead cell plus roomy context column that make `/tina/hitl` readable, and
 * the two facts `v_refresh_jobs` gained in tina migration `0061` for exactly this. `id`, `page_gone`
 * and `cost` are dropped per the owner's "columns job and page can be removed"; `admin_event_id`
 * and `changes` are not dropped so much as rebuilt — the Mood-event link is the lead cell's own
 * title and the id its subtitle, and `changes` was a count standing in for the kinds themselves.
 * Page-gone is not lost with its column: a park with nothing open IS a page-gone park on every live
 * row, so `review_kinds` renders the chip in that case.
 *
 * The `"run"` scope is deliberately untouched by all of this — `RefreshJobsTable` and the run show
 * page depend on it, so the review-only columns are module-level constants spliced around a
 * filtered copy rather than anything conditional inside the shared array.
 *
 * **Row density: where it actually comes from (R77 follow-up).** The owner still read the review
 * rows as shorter and harder to scan than `/tina/hitl`'s — "i want them the same as hitl queue" —
 * and the cause is neither padding nor line-height. Both pages mount the same `DataTable`, whose
 * `TableCell` is `py-0 px-1`: **vertical padding is zero on both sides**, so a row is exactly as
 * tall as its tallest cell. The figures below are off the real Tailwind v4 compiler, not read from
 * the docs — the numbers matter and the intuitive ones are wrong:
 *
 * - `text-xs` emits `font-size: var(--text-xs)` **and** `line-height: var(--text-xs--line-height)`,
 *   i.e. 0.75rem over `calc(1 / 0.75)` → a 12px glyph in a **16px** line box.
 * - `text-[11px]` emits `font-size: 11px` and **nothing else** — an arbitrary font-size utility
 *   carries no line-height — so the subtitle inherits the `<table>`'s own `text-sm` leading, which
 *   is the *unitless* `calc(1.25 / 0.875)` = 1.4286. Unitless leading inherits as a number and is
 *   re-multiplied by each element's own font-size, so the 11px subtitle is 11 × 1.4286 ≈
 *   **15.7px** — not the 20px the parent gets from the same declaration.
 * - So a HITL two-line block — `context`, `run` — is ≈ **31.7px**, and its `actions` cell holds a
 *   `ShowButton size="sm"`, which is `h-9` = `calc(0.25rem * 9)` = **36px**. The button is the
 *   taller of the two, so 36px is the HITL queue's row height on every row regardless of content.
 *
 * The review lead cell was already built to the two-line idiom, so it already measured that same
 * ≈31.7px. What was missing is the **floor**: with no `actions` column nothing in a review row
 * reached 36px, and a row whose lead cell had no subtitle — the `published_name`-absent fallback,
 * i.e. every row before the tina deploy — collapsed to a single 16px line while every HITL row
 * stayed at 36.
 *
 * Adding `actions` therefore fixes the density and the missing eye button as one change, which is
 * why they are one commit. `max-w-[260px]` on the lead cell matches `context`'s own width too, so
 * the only class the two blocks now differ by is `font-mono` on the id subtitle — a typeface, not a
 * metric. `__tests__/use-refresh-jobs-columns.test.tsx` pins all of this by rendering both hooks'
 * cells and comparing their class names, so it cannot drift back on eyeballing.
 */
export type RefreshJobsColumnScope = "run" | "review";

/**
 * The review queue's lead cell: the published event's name over its Mood id, both under one link.
 *
 * Shaped after `useItemsColumns`' `context` column, which is what makes the main HITL queue scan
 * as a list of events rather than a grid of ids: a `text-xs font-medium` title with a
 * `text-[11px] text-muted-foreground` subtitle beneath it.
 *
 * **Degrades on purpose.** `published_name` is absent against an orchestrator older than tina
 * migration `0061` and `null` on a job that never parked, and either way the title falls back to
 * the raw `admin_event_id` — the pre-R77 rendering — rather than an em dash. The subtitle is then
 * suppressed, because printing the same id twice is worse than printing it once.
 */
const REVIEW_EVENT_COLUMN: ColumnDef<RefreshJobRow> = {
  id: "event",
  header: "Event",
  cell: ({ row }) => {
    const { published_name: name, admin_event_id: adminEventId } = row.original;
    const label = name ?? adminEventId;
    if (!label) return <span className="text-muted-foreground text-xs">—</span>;
    const href = moodEventHref(adminEventId);
    const title = (
      <span
        className={cn(
          "block max-w-[260px] truncate text-xs font-medium",
          !name && "font-mono",
        )}
      >
        {label}
      </span>
    );
    return (
      <div className="min-w-0">
        {href ? (
          <a
            href={href}
            className="text-blue-500 hover:underline"
            onClick={(event) => event.stopPropagation()}
          >
            {title}
          </a>
        ) : (
          title
        )}
        {name && adminEventId ? (
          <span className="block max-w-[260px] truncate font-mono text-[11px] text-muted-foreground">
            {adminEventId}
          </span>
        ) : null}
      </div>
    );
  },
  enableSorting: false,
  enableColumnFilter: false,
};

/**
 * What the curator is actually being asked — the reason they can triage without opening the row.
 *
 * `review_kinds` is `hitl_context.problem.proposals`, i.e. what is still OPEN, and deliberately
 * not the array behind `proposal_count`, which also names the kinds `refresh_apply` already wrote
 * by itself. Labels go through `refreshChangeKindLabel` rather than a `Record` index so a kind this
 * build has no word for renders as itself.
 *
 * The three empty answers are three different things and read differently:
 *
 * - **absent** — an orchestrator older than tina migration `0061`, i.e. no such column: em dash;
 * - **empty on a page-gone park** — the chip the dropped `page_gone` column used to carry. On every
 *   parked row live as of 2026-09-08, all 54 with no open proposals are page-gone parks and all
 *   1,649 with proposals are not, so the two facts coincide — but the chip is still driven by
 *   `page_gone` itself, never inferred from emptiness;
 * - **empty otherwise** — em dash, because nothing in the row says what it is.
 */
/**
 * The eye button, last column, pinned right — a copy of `useItemsColumns`' own `actions` column,
 * down to `size: 70` and `ShowButton size="sm" hideText`.
 *
 * **An addition, not a replacement.** Double-clicking a row still opens it; this is the *visible*
 * way to do the same thing, which is what the HITL queue gives a curator and this page did not, so
 * the only discoverable affordance was a gesture nothing on screen mentioned. `resource` is
 * `tina-items` — the same target the double-click uses, because a refresh job IS an `events` row
 * and the panel registry routes `refresh_review` from the ordinary item show page.
 *
 * It is also what sets the row height. `size="sm"` renders `h-9` = 36px, the tallest thing in a
 * HITL row too, so every review row now has the same floor no matter what its other cells hold —
 * see the density note on this module for the measurements.
 */
const REVIEW_ACTIONS_COLUMN: ColumnDef<RefreshJobRow> = {
  id: "actions",
  size: 70,
  header: "Actions",
  cell: ({ row }) => (
    <ShowButton
      size="sm"
      hideText
      recordItemId={row.original.id}
      resource="tina-items"
    />
  ),
  enableSorting: false,
  enableColumnFilter: false,
};

const REVIEW_KINDS_COLUMN: ColumnDef<RefreshJobRow> = {
  id: "review_kinds",
  header: "Needs deciding",
  cell: ({ row }) => {
    const kinds = row.original.review_kinds;
    if (!kinds?.length) {
      if (!row.original.page_gone) {
        return <span className="text-muted-foreground text-xs">—</span>;
      }
      return makeTooltip(
        <Badge className="bg-orange-500/15 text-orange-700 dark:text-orange-300">
          <AlertTriangle className="mr-1 h-3 w-3" />
          Page gone
        </Badge>,
        row.original.page_gone_reason ??
          "Three consecutive misses, or a compare that said the page is about a different event",
      );
    }
    return (
      <div className="flex max-w-[260px] flex-wrap gap-1">
        {kinds.map((kind) => (
          <Badge
            key={kind}
            variant="secondary"
            className="text-[10px] font-normal"
          >
            {refreshChangeKindLabel(kind)}
          </Badge>
        ))}
      </div>
    );
  },
  enableSorting: false,
  enableColumnFilter: false,
};

export const useRefreshJobsColumns = (scope: RefreshJobsColumnScope = "run") =>
  React.useMemo<ColumnDef<RefreshJobRow>[]>(() => {
    const columns: ColumnDef<RefreshJobRow>[] = [
      {
        id: "id",
        accessorKey: "id",
        header: "Job",
        // The job IS an `events` row, so its id opens the ordinary item show
        // page — where the stage timeline, the artifacts and the HITL panel
        // already render a refresh row without any refresh-specific code.
        cell: ({ row }) =>
          makeTooltip(
            <span className="font-mono text-xs font-medium">
              {row.original.id.slice(0, 8)}
            </span>,
            `${row.original.id} · double-click the row to open it`,
          ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <ItemStatusBadge status={row.original.status} />,
        enableSorting: false,
        meta: {
          filterType: "select",
          options: [
            { label: "Pending", value: "pending" },
            { label: "In progress", value: "in_progress" },
            { label: "Retryable", value: "failed_retryable" },
            { label: "Needs review", value: "hitl" },
            { label: "Done", value: "done" },
            { label: "Skipped", value: "skipped" },
            { label: "Failed", value: "failed" },
            { label: "Cancelled", value: "cancelled" },
          ],
        },
      },
      {
        id: "refresh_outcome",
        accessorKey: "refresh_outcome",
        header: "Outcome",
        // NULL is "still in flight", which is data and not a gap — see
        // `refreshOutcomeLabel`. The filter cannot express it (the route takes
        // one outcome value, and there is no `is null` param), so the tile row
        // above the table owns that slice.
        cell: ({ row }) => (
          <Badge
            className={cn(
              refreshOutcomeClasses(row.original.refresh_outcome),
              !row.original.refresh_outcome && "font-normal",
            )}
          >
            {refreshOutcomeLabel(row.original.refresh_outcome)}
          </Badge>
        ),
        enableSorting: false,
        meta: {
          filterType: "select",
          options: REFRESH_OUTCOME_FILTER_OPTIONS.map(({ value, label }) => ({
            value,
            label,
          })),
        },
      },
      {
        id: "mode",
        accessorKey: "mode",
        header: "Mode",
        cell: ({ row }) =>
          makeTooltip(
            <Badge variant="secondary" className="text-[10px] font-normal">
              {refreshModeLabel(row.original.mode)}
            </Badge>,
            row.original.mode === "dup"
              ? "Seeded by the duplicate hook: another site's page was caught as a duplicate of this published event"
              : "Seeded by the nightly sweep: this event's own source page came due",
          ),
        enableSorting: false,
        // On the review page the toolbar owns this axis, and a `permanent`
        // entry would override anything the column filter set — an inert
        // control is worse than none.
        enableColumnFilter: scope !== "review",
        meta: {
          filterType: "select",
          options: REFRESH_MODE_FILTER_OPTIONS.map(({ value, label }) => ({
            value,
            label,
          })),
        },
      },
      {
        id: "site",
        accessorKey: "site",
        header: "Site",
        cell: ({ row }) => (
          <span className="truncate text-xs">{row.original.site}</span>
        ),
        enableSorting: false,
        // Free-text on purpose: the route matches `site` exactly and the run's
        // sites are not enumerated anywhere on this page, so a select would
        // have to guess its own options.
        meta: { filterType: "text" },
      },
      {
        id: "admin_event_id",
        header: "Mood event",
        // The point of the whole row: which published event this job is about.
        // Linked by id, never through `problem.ims_url` — Tina stamps that as
        // `/events/show/:id`, a route IMS declares but never mounts.
        cell: ({ row }) => {
          const href = moodEventHref(row.original.admin_event_id);
          if (!href || !row.original.admin_event_id) {
            return <span className="text-muted-foreground text-xs">—</span>;
          }
          return (
            <a
              href={href}
              className="group inline-flex items-center gap-1 font-mono text-xs hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.original.admin_event_id.slice(0, 10)}
              <ArrowUpRight className="h-3 w-3 shrink-0 text-muted-foreground" />
            </a>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "url",
        accessorKey: "url",
        header: "Source",
        // Null on a carry-site job that was seeded not-listed, and on nothing
        // else — the discover seeds themselves are excluded from the view.
        cell: ({ row }) => {
          const url = row.original.url;
          if (!url)
            return <span className="text-muted-foreground text-xs">—</span>;
          return makeTooltip(
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block max-w-[220px] truncate text-xs hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {url.replace(/^https?:\/\//, "")}
            </a>,
            url,
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "changes",
        header: "Proposed / applied",
        // Two numbers, one cell: the gap between them is the whole policy —
        // Tina fills blanks itself (applied) and asks before overwriting
        // anything already there (proposed, then parked).
        cell: ({ row }) => {
          const { proposal_count: proposed, applied_count: applied } =
            row.original;
          if (proposed === 0 && applied === 0) {
            return <span className="text-muted-foreground text-xs">—</span>;
          }
          return makeTooltip(
            <span className="font-mono text-xs tabular-nums">
              {proposed} / <span className="text-emerald-600">{applied}</span>
            </span>,
            `${proposed} change(s) proposed by the compare stage · ${applied} written to the published event`,
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "page_gone",
        header: "Page",
        // Only ever rendered when true: a "no" chip on every healthy row would
        // be noise on the column that matters most when it fires.
        cell: ({ row }) => {
          if (!row.original.page_gone) {
            return <span className="text-muted-foreground text-xs">—</span>;
          }
          return makeTooltip(
            <Badge className="bg-orange-500/15 text-orange-700 dark:text-orange-300">
              <AlertTriangle className="mr-1 h-3 w-3" />
              Gone
            </Badge>,
            row.original.page_gone_reason ??
              "Three consecutive misses, or a compare that said the page is about a different event",
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "cost",
        accessorKey: "cost",
        header: "Cost",
        cell: ({ row }) => (
          <span
            className={cn(
              "font-mono text-xs tabular-nums",
              Number(row.original.cost) === 0 && "text-muted-foreground",
            )}
          >
            {formatUsdCost(row.original.cost)}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Seeded",
        // The run drill-down is one night, so the clock alone identifies a job;
        // the review queue spans every night a park is still open, where an
        // hour with no date is ambiguous.
        cell: ({ row }) =>
          makeTooltip(
            <span className="font-mono text-xs tabular-nums">
              {dayjs(row.original.created_at).format(
                scope === "review" ? "DD/MM HH:mm" : "HH:mm:ss",
              )}
            </span>,
            row.original.finished_at
              ? `seeded ${dayjs(row.original.created_at).format("DD/MM/YYYY HH:mm:ss")} · finished ${dayjs(row.original.finished_at).format("HH:mm:ss")}`
              : `seeded ${dayjs(row.original.created_at).format("DD/MM/YYYY HH:mm:ss")} · not finished`,
          ),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ];
    if (scope !== "review") return columns;
    // Dropped for the review queue (R77). Three groups, three reasons:
    //   - `status`/`refresh_outcome` are constant on every row of a `status=hitl`
    //     page — the status IS the pin, and `v_refresh_jobs` derives
    //     `refresh_outcome='hitl'` from it — while carrying a filter the pinned
    //     `permanent` entry would override;
    //   - `id`/`page_gone`/`cost` are ops columns the owner asked off this page. The
    //     job id is on the item page the row double-clicks open to, and page-gone
    //     survives inside `review_kinds`;
    //   - `admin_event_id`/`changes` are superseded rather than removed: the Mood
    //     event is `event`'s own link and subtitle, and `changes` was a pair of
    //     counts standing in for the kinds `review_kinds` now names.
    const dropped = new Set([
      "id",
      "status",
      "refresh_outcome",
      "admin_event_id",
      "changes",
      "page_gone",
      "cost",
    ]);
    // Built around a filtered copy rather than by mutating the shared array: the
    // two lead columns are what a curator reads first, `actions` is last and
    // pinned right by the page, and `[lead..., ...kept, actions]` leaves the
    // `"run"` array above untouched by construction.
    return [
      REVIEW_EVENT_COLUMN,
      REVIEW_KINDS_COLUMN,
      ...columns.filter((column) => !dropped.has(String(column.id))),
      REVIEW_ACTIONS_COLUMN,
    ];
  }, [scope]);
